import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import {
  getClass,
  getStudents,
  getAllStudentsResultsPerAssessment,
  updateUserActivity,
  getCountryId
} from "../services/APIClient";
import HeadingBanner from "../components/HeadingBanner";
import OrientationPrompt from "../components/OrientationPrompt";
import { Container, Row, Col } from "react-bootstrap";
import { BigSpinner } from "../services/SpinnerComponent";

// Lazy load components for better performance
const LeftPanel = React.lazy(() => import("../components/LeftPanel"));
const StudentsResultsOverview = React.lazy(() =>
  import("../components/StudentsResultsOverview")
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in component:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;

      return (
        <div
          style={{
            padding: "1em",
            backgroundColor: "#f8d7da",
            color: "#842029",
            borderRadius: "5px",
          }}
        >
          <h2>Oops! Something went wrong.</h2>
          <p>
            This error is most likely due to a cache issue. Please try refreshing your browser with a hard reload:
          </p>
          <ul style={{ marginBottom: "1em" }}>
            <li><strong>PC:</strong> Press Ctrl + Shift + R</li>
            <li><strong>Mac:</strong> Press Command (⌘) + Shift + R</li>
          </ul>
          <p>
            If the problem persists after clearing your cache, please contact us at{" "}
            <a href="mailto:info@alpaca-assessment.com">
              info@alpaca-assessment.com
            </a>{" "}
            with the following details:
          </p>
          <details style={{ whiteSpace: "pre-wrap", marginTop: "1em" }}>
            <summary>Error Details</summary>
            <p>
              <strong>Error:</strong> {error?.toString()}
            </p>
            <p>
              <strong>Stack Trace:</strong>
              <br />
              {errorInfo?.componentStack ||
                "No additional information available."}
            </p>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

function StudentsResults() {
  const location = useLocation();

  console.log("=== STUDENTS RESULTS COMPONENT DEBUG START ===");
  console.log("Full location object:", location);
  console.log("Location state:", location.state);

  const name = "Results";
  const locationState = location.state || {};
  const classDetails = locationState.classDetails || {};
  const gradeLevel = locationState.gradeLevel || "Unknown Grade";
  const classAssessments = locationState.classAssessments || [];
  const teachers = locationState.teachers || [];
  const assessment = locationState.assessment || {};
  const assessmentInstances = assessment.instances || {};
  const assessmentId = assessment.assessmentid || "";

  console.log("=== EXTRACTED DATA ===");
  console.log("classDetails:", classDetails);
  console.log("Is snapshot class?", classDetails.snapshot);
  console.log("gradeLevel:", gradeLevel);
  console.log("classAssessments:", classAssessments);
  console.log("classAssessments length:", classAssessments.length);
  console.log("teachers:", teachers);
  console.log("teachers length:", teachers.length);
  console.log("assessment:", assessment);
  console.log("assessment keys:", Object.keys(assessment));
  console.log("assessment.instances:", assessment.instances);
  console.log("assessment.instances type:", typeof assessment.instances);
  console.log("assessment.instances Array?", Array.isArray(assessment.instances));
  console.log("assessmentInstances (extracted):", assessmentInstances);
  console.log("assessmentInstances type:", typeof assessmentInstances);
  console.log("assessmentId:", assessmentId);

  // Log individual assessment instances if it's an array
  if (Array.isArray(assessment.instances)) {
    console.log("Individual assessment instances:");
    assessment.instances.forEach((instance, index) => {
      console.log(`  Instance ${index}:`, instance);
    });
  }

  const schoolid = locationState.schoolid || "";
  const teacherid = locationState?.teacherid || "";
  const classid = classDetails.id || "";

  console.log("=== IDs ===");
  console.log("schoolid:", schoolid);
  console.log("teacherid:", teacherid);
  console.log("classid:", classid);

  const [studentsResponse, setStudentsResponse] = useState([]);
  const [rawStudentResults, setRawStudentResults] = useState({});
  const [studentResults, setStudentResults] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countryId, setCountryId] = useState(null); // Add this

  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  // Memoized `studentIds` to avoid unnecessary recalculations
  const studentIds = useMemo(
    () => {
      const ids = studentsResponse.map((student) => student.id);
      console.log("=== MEMOIZED STUDENT IDS ===");
      console.log("studentsResponse:", studentsResponse);
      console.log("Extracted student IDs:", ids);
      return ids;
    },
    [studentsResponse]
  );

  // Fetch students based on `classid`
  useEffect(() => {
    console.log("=== FIRST useEffect - Fetching Students ===");
    console.log("classid:", classid);

    if (!classid) {
      console.log("No classid, skipping student fetch");
      return;
    }

    console.log("Calling getStudents API with classid:", classid);

    getStudents(classid)
      .then((response) => {
        console.log("getStudents API response:", response);
        console.log("Students array:", response?.students);
        console.log("Students count:", response?.students?.length);

        setStudentsResponse(response?.students || []);
      })
      .catch((err) => {
        console.error("Failed to fetch students:", err);
        console.error("Error details:", err.message, err.response);
        setError("Failed to fetch students.");
      });
  }, [classid]);

  useEffect(() => {
    console.log("=== SECOND useEffect - Fetching Assessment Results ===");
    console.log("classid:", classid);
    console.log("assessmentInstances:", assessmentInstances);
    console.log("assessmentInstances empty check:", !assessmentInstances);

    if (!classid || !assessmentInstances) {
      console.log("Missing classid or assessmentInstances, skipping fetch");
      return;
    }

    const fetchData = async () => {
      console.log("=== Starting fetchData async function ===");

      try {
        // Fetch students first
        console.log("Step 1: Fetching students with getStudents API");
        const studentsResponse = await getStudents(classid);
        console.log("Students API response:", studentsResponse);
        console.log("Students array from API:", studentsResponse?.students);

        setStudentsResponse(studentsResponse?.students || []);

        // Fetch results based on fetched students
        const studentIds = (studentsResponse?.students || []).map(
          (student) => student.id
        );

        console.log("Step 2: Extracted student IDs for results API:", studentIds);
        console.log("Step 3: About to call getAllStudentsResultsPerAssessment");
        console.log("  - studentIds:", studentIds);
        console.log("  - assessmentInstances (raw):", assessmentInstances);

        // FIXED: Extract just the IDs from assessment instances
        let assessmentInstanceIds;
        if (Array.isArray(assessmentInstances)) {
          console.log("=== DEBUGGING ASSESSMENT INSTANCES ===");
          console.log("assessmentInstances length:", assessmentInstances.length);
          console.log("Sample instances:", assessmentInstances.slice(0, 3));

          // assessmentInstances is already an array of UUID strings, no need to map
          assessmentInstanceIds = assessmentInstances;
          console.log("  - Using assessment instance IDs directly:", assessmentInstanceIds);
        } else {
          // Fallback for non-array data
          assessmentInstanceIds = assessmentInstances;
          console.log("  - Using assessmentInstances as-is:", assessmentInstanceIds);
        }

        const studentResults = await getAllStudentsResultsPerAssessment(
          studentIds,
          assessmentInstanceIds  // FIXED: Use extracted IDs instead of full objects
        );

        setRawStudentResults(studentResults);

        console.log("=== getAllStudentsResultsPerAssessment API RESPONSE ===");
        console.log("studentResults:", studentResults);
        console.log("studentResults type:", typeof studentResults);
        console.log("studentResults Array?", Array.isArray(studentResults));
        console.log("studentResults length:", studentResults?.length);

        if (studentResults && studentResults.length > 0) {
          console.log("Sample result item:", studentResults[0]);
        }

        // FIXED: Transform keyed object to expected array format
        let transformedResults;
        if (studentResults && typeof studentResults === 'object' && !Array.isArray(studentResults)) {
          console.log("=== TRANSFORMING KEYED OBJECT TO ARRAY FORMAT ===");

          transformedResults = Object.entries(studentResults).map(([studentId, assessments]) => {
            // Find the student data
            const student = studentsResponse?.students?.find(s => s.id === studentId) || {
              id: studentId,
              name: 'Unknown Student'
            };

            // Get the tasks from the first (and should be only) assessment
            const assessment = assessments[0] || {};
            const tasks = assessment.tasks || [];

            console.log(`Transforming student ${studentId}:`, {
              student: student.name,
              tasksCount: tasks.length,
              assessment: assessment.name
            });

            return {
              student: student,
              tasks: tasks,
              completed: tasks.length > 0, // Basic completion logic
              assessmentInstance: assessment.id
            };
          });

          console.log("Transformed results array:", transformedResults);
          console.log("Transformed results length:", transformedResults.length);

        } else {
          console.log("studentResults is already in array format or empty");
          transformedResults = studentResults || [];
        }

        setStudentResults(transformedResults);

        console.log("=== fetchData completed successfully ===");
      } catch (err) {
        console.error("=== ERROR in fetchData ===");
        console.error("Error:", err);
        console.error("Error message:", err.message);
        console.error("Error response:", err.response);
        console.error("Error stack:", err.stack);
        setError("Failed to fetch data.");
      }
    };

    fetchData();
  }, [classid, assessmentInstances]);

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  // Fetch country ID based on schoolid
useEffect(() => {
  console.log("=== FETCHING COUNTRY ID ===");
  console.log("schoolid:", schoolid);

  if (schoolid) {
    getCountryId(schoolid)
      .then((response) => {
        console.log("getCountryId response:", response);
        if (response.countryId) {
          console.log("Country ID found:", response.countryId);
          setCountryId(response.countryId);
        } else {
          console.warn("No country ID found for school:", response.message);
          setCountryId(null);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch country ID:", error);
        setCountryId(null);
      });
  }
}, [schoolid]);

  console.log("=== FINAL STATE BEFORE RENDER ===");
  console.log("studentsResponse:", studentsResponse);
  console.log("studentResults:", studentResults);
  console.log("error:", error);
  console.log("loading:", loading);

  console.log("=== DATA BEING PASSED TO CHILD COMPONENT ===");
  console.log("StudentsResultsOverview props:");
  console.log("  - classDetails:", classDetails);
  console.log("  - classAssessments:", classAssessments);
  console.log("  - students:", studentsResponse);
  console.log("  - assessment:", assessment);
  console.log("  - assessmentInstanceID:", assessmentInstances);
  console.log("  - studentResults:", studentResults);

  return (
    <Container fluid className="vh-100">
      <HeadingBanner
        name={name || "Results"}
        isLoggedIn={true}
        cloud={true}
        teacherid={teacherid || "N/A"}
      />
      <Row className="h-100">
        <Suspense fallback={<BigSpinner />}>
          <ErrorBoundary>
            <LeftPanel />
          </ErrorBoundary>
        </Suspense>
        <Col sm={{ span: 10, offset: 2 }} className="blue-background">
          <Suspense fallback={<BigSpinner />}>
            <StudentsResultsOverview
              classDetails={classDetails}
              classAssessments={
                Array.isArray(classAssessments) ? classAssessments : []
              }
              students={studentsResponse}
              gradeLevel={gradeLevel}
              teachers={teachers}
              assessment={assessment}
              assessmentInstanceID={assessmentInstances}
              schoolid={schoolid}
              teacherid={teacherid}
              studentResults={studentResults}
              assessmentId={assessmentId}
              rawStudentResults={rawStudentResults}
              countryId={countryId}
            />
          </Suspense>
        </Col>
      </Row>
      {isPortrait && <OrientationPrompt />}
    </Container>
  );
}

export default StudentsResults;

// StudentsResults.propTypes = {
//   location: PropTypes.shape({
//     state: PropTypes.shape({
//       classDetails: PropTypes.object,
//       gradeLevel: PropTypes.string,
//       classAssessments: PropTypes.array,
//       teachers: PropTypes.array,
//       assessment: PropTypes.object,
//       assessmentInstanceID: PropTypes.string,
//       schoolid: PropTypes.string,
//       teacherid: PropTypes.string,
//     }),
//   }),
// };

// export default StudentsResults;

// import { React, useState, useEffect } from "react";

// import { useLocation, useParams } from "react-router-dom";

// import {
//   getClass,
//   getStudents,
//   getAllStudentsResultsPerAssessment,
//   updateUserActivity
// } from "../services/APIClient";

// import HeadingBanner from "../components/HeadingBanner";
// import LeftPanel from "../components/LeftPanel";
// import StudentsResultsOverview from "../components/StudentsResultsOverview";
// import OrientationPrompt from "../components/OrientationPrompt";

// import { Container, Row, Col } from "react-bootstrap";

// function StudentsResults(props) {
//   const location = useLocation();

//   let name = "Results";
//   console.log(location, "LOCATION");

//   // Handle undefined state
//   let classDetails = location.state?.classDetails?.classDetails || {};
//   let gradeLevel = location.state?.gradeLevel?.gradeLevel || "";
//   let classAssessments =
//     location.state?.classAssessments?.classAssessments || [];
//   // let teacherid = classDetails.teacherId || "";
//   let teachers = location.state?.teachers?.teachers || [];
//   let assessment = location.state?.assessment?.assessment || {};
//   let assessmentInstanceID =
//     location.state?.assessmentInstanceID?.assessmentInstanceID || "";
//   let assessmentInstances =
//     location.state?.assessment?.assessment.instances || {};

//   const [studentsResponse, setStudentsResponse] = useState("");
//   const [error, setError] = useState(null);
//   const [studentIds, setStudentIds] = useState([]);
//   const [studentResults, setStudentResults] = useState([]);

//   console.log(classAssessments, "STATUS");

//   let schoolid = location.state?.schoolid?.schoolid;
//   let teacherid = location.state?.teacherid?.teacherid;
//   let classid = classDetails.id;

//   useEffect(() => {
//     getStudents(classid).then((response) =>
//       setStudentsResponse(response.students)
//     );
//   }, [classid]);

//   const [isPortrait, setIsPortrait] = useState(
//     window.matchMedia("(orientation: portrait)").matches
//   );

//   useEffect(() => {
//     const handleOrientationChange = () => {
//       setIsPortrait(window.innerWidth < window.innerHeight);
//     };

//     window.addEventListener("resize", handleOrientationChange);

//     return () => {
//       window.removeEventListener("resize", handleOrientationChange);
//     };
//   }, []);

//   useEffect(() => {
//     window.HubSpotConversations?.widget?.refresh();
//   }, []);

//   useEffect(() => {
//     window.HubSpotConversations?.widget?.refresh();
//   }, []);

//   useEffect(() => {
//     // Extract student IDs whenever studentsResponse changes
//     if (Array.isArray(studentsResponse)) {
//       const ids = studentsResponse.map((student) => student.id);
//       setStudentIds(ids);
//     }
//   }, [studentsResponse]);

//   useEffect(() => {
//     // Fetch results when studentIds and assessmentInstanceID are available

//     getAllStudentsResultsPerAssessment(studentIds, assessmentInstances)
//       .then((data) => {
//         setStudentResults(data);
//         console.log("fired");
//       })
//       .catch((error) => {
//         console.error("Error fetching results:", error);
//         setError("Error fetching results");
//       });
//   }, [studentIds, assessmentInstances]);

//   useEffect(() => {
//     // Fetch results when studentIds and assessmentInstanceID are available

//     updateUserActivity(teacherid, "results-viewed")
//   }, [studentIds, assessmentInstances]);

//   return (
//     <Container fluid className="vh-100">
//       <HeadingBanner
//         name={name}
//         isLoggedIn={true}
//         cloud={true}
//         teacherid={teacherid}
//       />
//       <Row className="h-100">
//         <LeftPanel />
//         <Col sm={{ span: 10, offset: 2 }} className="blue-background">
//           <StudentsResultsOverview
//             classDetails={classDetails}
//             classAssessments={classAssessments}
//             // teachers={location.state.teachers}
//             // sharedWith={sharedWith}
//             students={studentsResponse}
//             gradeLevel={gradeLevel}
//             teachers={teachers}
//             assessment={assessment}
//             assessmentInstanceID={assessmentInstanceID}
//             schoolid={schoolid}
//             teacherid={teacherid}
//             studentResults={studentResults}
//             // name={location.state.name.name}
//           />
//         </Col>
//       </Row>
//       {isPortrait && <OrientationPrompt />}
//     </Container>
//   );
// }

// export default StudentsResults;
